'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { X, Clock3, Megaphone, Sparkles, Tag } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { TrackableVideoEmbed } from '@/components/media/TrackableVideoEmbed'
import { getImageCandidates } from '@/lib/image'
import { API_CONFIG } from '@/config/api'

export type AnnouncementType = 'welcome' | 'promo' | 'announcement'
export type AnnouncementPreset =
  | 'welcome_video'
  | 'welcome_simple'
  | 'offer_limited'
  | 'community_notice'
export type AnnouncementDisplayMode =
  | 'once_per_user'
  | 'always'
  | 'manual_force'
export type AnnouncementAction = 'none' | 'link' | 'product' | 'close'

export type AnnouncementItem = {
  announcement_id: number
  seller_id: number
  name: string
  type: AnnouncementType
  preset: AnnouncementPreset
  title: string
  message: string
  image: string
  video: string
  action: AnnouncementAction
  button_text: string
  button_link: string
  status: number
  display_mode: AnnouncementDisplayMode
  audience: 'all'
  is_default: number
  is_global_active: number
  product_id: number
  override_price: string
  override_compare_price: string
  discount_label: string
  countdown_enabled: number
  countdown_mode: 'manual' | 'product_discount'
  countdown_end_at: string | null
  start_at: string | null
  end_at: string | null
  sort_order: number
}

type Props = {
  open: boolean
  item: AnnouncementItem | null
  onOpenChange: (open: boolean) => void
  onClose?: () => void
  onAction?: (item: AnnouncementItem) => void
}

function formatMoney(value: string | number) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return null
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 2,
  }).format(n)
}

function hasVisualMedia(item: AnnouncementItem | null) {
  if (!item) return false
  return !!item.video || !!item.image
}

function formatCountdownParts(target: string | null) {
  if (!target) return null

  const end = new Date(target).getTime()
  const now = Date.now()
  const diff = end - now

  if (Number.isNaN(end) || diff <= 0) {
    return {
      expired: true,
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
    }
  }

  const totalSeconds = Math.floor(diff / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return {
    expired: false,
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
  }
}

function CountdownBlock({ endAt }: { endAt: string | null }) {
  const [tick, setTick] = useState(() => formatCountdownParts(endAt))

  useEffect(() => {
    setTick(formatCountdownParts(endAt))
    if (!endAt) return

    const id = window.setInterval(() => {
      setTick(formatCountdownParts(endAt))
    }, 1000)

    return () => window.clearInterval(id)
  }, [endAt])

  if (!endAt || !tick) return null

  return (
    <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
        <Clock3 className="h-4 w-4" />
        {tick.expired ? 'La promoción terminó' : 'La promoción termina en'}
      </div>

      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Días', value: tick.days },
          { label: 'Horas', value: tick.hours },
          { label: 'Min', value: tick.minutes },
          { label: 'Seg', value: tick.seconds },
        ].map((part) => (
          <div
            key={part.label}
            className="rounded-xl border bg-background/80 px-3 py-3 text-center"
          >
            <div className="text-lg font-bold">{part.value}</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              {part.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function MediaBlock({ item }: { item: AnnouncementItem }) {
  const [duration, setDuration] = useState(0)
  const [lastProgressSentAt, setLastProgressSentAt] = useState(0)
  const [lastProgressPosition, setLastProgressPosition] = useState(0)

  const candidates = getImageCandidates(item.image, {
    siteOrigin: API_CONFIG.BASE_URL,
    assetBaseUrl: process.env.NEXT_PUBLIC_ASSET_BASE_URL,
  })

  const [index, setIndex] = useState(0)
  const src = candidates[index] || ''

  if (item.video) {
    return (
      <div className="mb-4">
        <TrackableVideoEmbed
          url={item.video}
          rounded="2xl"
          variant="plain"
          controls
          autoplay={false}
          muted={false}
          className="aspect-video w-full"
          onDuration={(seconds) => {
            setDuration(Number(seconds || 0))
          }}
          onPlay={(seconds = 0) => {
            postVideoEvent({
              announcement_id: item.announcement_id,
              event: 'play',
              position_seconds: Number(seconds || 0),
              duration_seconds: duration,
            })
          }}
          onPause={(seconds = 0) => {
            postVideoEvent({
              announcement_id: item.announcement_id,
              event: 'pause',
              position_seconds: Number(seconds || 0),
              duration_seconds: duration,
            })
          }}
          onEnded={() => {
            postVideoEvent({
              announcement_id: item.announcement_id,
              event: 'ended',
              position_seconds: duration,
              duration_seconds: duration,
            })
          }}
          onTimeUpdate={(seconds = 0) => {
            const now = Date.now()
            const current = Number(seconds || 0)

            const enoughTimePassed = now - lastProgressSentAt >= 10000
            const enoughPositionMoved =
              Math.abs(current - lastProgressPosition) >= 10

            if (!enoughTimePassed && !enoughPositionMoved) return

            const delta = Math.max(0, current - lastProgressPosition)

            setLastProgressSentAt(now)
            setLastProgressPosition(current)

            postVideoEvent({
              announcement_id: item.announcement_id,
              event: 'progress',
              position_seconds: current,
              duration_seconds: duration,
              watch_time_delta_seconds: delta,
            })
          }}
        />
      </div>
    )
  }

  if (item.image) {
    return (
      <div className="overflow-hidden rounded-2xl border bg-muted/30">
        <div className="aspect-[16/9] w-full overflow-hidden">
          <img
            src={src}
            alt="Announcement"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    )
  }

  return null
}

function OfferCard({ item }: { item: AnnouncementItem }) {
  const price = formatMoney(item.override_price)
  const comparePrice = formatMoney(item.override_compare_price)

  const hasPrice = Number(item.override_price || 0) > 0
  const hasCompare = Number(item.override_compare_price || 0) > 0

  if (!hasPrice && !item.discount_label && !item.countdown_enabled) {
    return null
  }

  return (
    <div className="space-y-4 rounded-2xl border border-primary/15 bg-primary/5 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-background px-3 py-1 text-xs font-semibold">
          <Tag className="h-3.5 w-3.5" />
          Oferta especial
        </span>

        {item.discount_label ? (
          <span className="rounded-full bg-rose-500 px-3 py-1 text-xs font-bold text-white">
            {item.discount_label}
          </span>
        ) : null}
      </div>

      {(hasPrice || hasCompare) && (
        <div className="flex flex-wrap items-end gap-3">
          {hasPrice ? <div className="text-3xl font-bold">{price}</div> : null}
          {hasCompare ? (
            <div className="pb-1 text-sm text-muted-foreground line-through">
              {comparePrice}
            </div>
          ) : null}
        </div>
      )}

      {item.countdown_enabled ? (
        <CountdownBlock endAt={item.countdown_end_at} />
      ) : null}
    </div>
  )
}

async function postVideoEvent(body: {
  announcement_id: number
  event: 'play' | 'pause' | 'ended' | 'progress'
  position_seconds?: number
  duration_seconds?: number
  watch_time_delta_seconds?: number
}) {
  try {
    await fetch('/api/announcements/video-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (err) {
    console.error('Announcement video event error:', err)
  }
}

export default function AnnouncementModal({
  open,
  item,
  onOpenChange,
  onClose,
  onAction,
}: Props) {
  const isOffer = item?.preset === 'offer_limited'
  const isWelcome =
    item?.preset === 'welcome_video' || item?.preset === 'welcome_simple'

  const ctaText = useMemo(() => {
    if (!item) return ''
    return item.button_text?.trim() || 'Continuar'
  }, [item])

  if (!item) return null

  const hasMedia = hasVisualMedia(item)
  const useTwoColumns = !!item.video

  return (
    <Dialog
    open={open}
    onOpenChange={(next) => {
        if (!next) {
        onOpenChange(false)
        onClose?.()
        return
        }

        onOpenChange(true)
    }}
    >
      <DialogContent
        className={cn(
            'max-h-[92vh] overflow-y-auto border-0 p-0 shadow-2xl',
            'sm:max-w-3xl'
        )}
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        >
        <div className="relative overflow-hidden rounded-3xl bg-background">
          <button
            type="button"
            onClick={() => {
              onOpenChange(false)
              onClose?.()
            }}
            className="absolute right-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border bg-background/90 text-foreground shadow-sm transition hover:bg-background"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>

          <div
            className={cn(
                'grid gap-0',
                useTwoColumns ? 'md:grid-cols-2' : 'md:grid-cols-1'
            )}
            >
            {hasMedia ? (
                <div className={cn(
                  'p-6 md:p-7 border-b',
                  useTwoColumns && 'md:border-b-0 md:border-r'
                )}>
                <MediaBlock item={item} />

                {useTwoColumns && isOffer ? <OfferCard item={item} /> : null}

                {useTwoColumns && !isOffer && item.countdown_enabled ? (
                  <CountdownBlock endAt={item.countdown_end_at} />
                ) : null}
                </div>
            ) : null}

            <div
                className={cn(
                'flex flex-col justify-between p-6 md:p-7',
                !hasMedia && 'mx-auto w-full max-w-2xl'
                )}
            >
              <div className="space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                  {item.is_global_active ? (
                    <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                      Anuncio global
                    </span>
                  ) : null}

                  {isOffer ? (
                    <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                      Promoción limitada
                    </span>
                  ) : null}

                  {isWelcome ? (
                    <span className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold">
                      <Sparkles className="h-3.5 w-3.5" />
                      Mensaje especial
                    </span>
                  ) : null}
                </div>

                <div className="space-y-3">
                  <h2 className="text-2xl font-bold leading-tight md:text-3xl">
                    {item.title}
                  </h2>

                  <div className="whitespace-pre-line text-sm leading-6 text-muted-foreground md:text-base">
                    {item.message}
                  </div>
                </div>

                {!useTwoColumns && isOffer ? <OfferCard item={item} /> : null}

                {!useTwoColumns && !isOffer && item.countdown_enabled ? (
                  <CountdownBlock endAt={item.countdown_end_at} />
                ) : null}
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                {item.action !== 'none' && item.action !== 'close' ? (
                  <Button
                    className="w-full sm:flex-1"
                    onClick={() => onAction?.(item)}
                  >
                    {ctaText}
                  </Button>
                ) : null}

                <Button
                  variant={item.action === 'none' || item.action === 'close' ? 'default' : 'outline'}
                  className="w-full sm:w-auto"
                  onClick={() => {
                    onOpenChange(false)
                    onClose?.()
                  }}
                >
                  {item.action === 'none' || item.action === 'close'
                    ? ctaText || 'Entendido'
                    : 'Cerrar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}